import {
  ApiError,
  projectPlanningControllerApplySuggestedWorkloadCategories,
  projectPlanningControllerArchiveWorkOrders,
  projectPlanningControllerBatchAddWorkOrders,
  projectPlanningControllerBatchRemoveWorkOrders,
  projectPlanningControllerConfirmProductWorkloadCategory,
  projectPlanningControllerCreateProjectPlanning,
  projectPlanningControllerGetProjectPlanning,
  projectPlanningControllerListProjectsPlanning,
  projectPlanningControllerReleaseDraftWorkOrders,
  projectPlanningControllerReleaseProject,
  projectPlanningControllerUnarchiveWorkOrders,
  projectPlanningControllerUpdateProjectColumnConfig,
  projectPlanningControllerUpdateProjectPlanning,
  workOrdersPartnerControllerReopenForEdit,
  workOrdersPartnerControllerReRelease,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { BILL_OF_OPERATIONS_KEY } from '../bill-of-operations/queries';
import { PRODUCTION_PLANNER_KEY } from '../production-planner/queries';
import { type BatchProgress, PROJECT_PLANNING_BATCH_CHUNK_SIZE } from './constants';
import type {
  ProjectColumnState,
  ProjectPlanningDetail,
  ProjectPlanningListItem,
  ProjectWorkOrderDetail,
} from './types';

export const PROJECT_PLANNING_KEY = 'project-planning' as const;

// ─── Project planning list ───────────────────────────────────────

export function projectPlanningListQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [PROJECT_PLANNING_KEY, 'list', partnerId] as const,
    queryFn: async ({ signal }) => {
      const res = await projectPlanningControllerListProjectsPlanning(
        { partner_id: partnerId ?? '' },
        { signal },
      );
      return res.data as ProjectPlanningListItem[];
    },
    enabled: !!partnerId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Project planning detail ─────────────────────────────────────

export function projectPlanningDetailQueryOptions(projectId: string | undefined) {
  return queryOptions({
    queryKey: [PROJECT_PLANNING_KEY, 'detail', projectId] as const,
    queryFn: async ({ signal }) => {
      if (!projectId) throw new Error('projectId is required');
      const res = await projectPlanningControllerGetProjectPlanning(projectId, undefined, {
        signal,
      });
      return res.data as ProjectPlanningDetail;
    },
    enabled: !!projectId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Archived work orders (additive, for "Show archived") ────────
//
// Kept separate from the main detail query so the editor's optimistic
// updates (use-project-sync, batch add/remove) keep writing to the
// untouched `[KEY, 'detail', projectId]` cache entry. The archived rows
// are fetched only when the toggle is on and merged in for display. The
// queryKey nests under the detail prefix so the archive/unarchive
// mutations' `[KEY, 'detail', projectId]` invalidation refreshes it too.

export function projectArchivedWorkOrdersQueryOptions(
  projectId: string | undefined,
  enabled: boolean,
) {
  return queryOptions({
    queryKey: [PROJECT_PLANNING_KEY, 'detail', projectId, 'archived'] as const,
    queryFn: async ({ signal }) => {
      if (!projectId) throw new Error('projectId is required');
      const res = await projectPlanningControllerGetProjectPlanning(
        projectId,
        { include_archived: true },
        { signal },
      );
      const detail = res.data as ProjectPlanningDetail;
      return detail.work_orders.filter((wo) => wo.work_order_current_status === 'archived');
    },
    enabled: !!projectId && enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Create project mutation ─────────────────────────────────────

export interface CreateProjectParams {
  partner_id: string;
  project_name: string;
  project_code: string;
  project_prefix?: string | null;
  project_scheduled_start_date?: string | null;
  project_scheduled_complete_date?: string | null;
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateProjectParams) => {
      const res = await projectPlanningControllerCreateProjectPlanning(params);
      return res.data as ProjectPlanningListItem;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'list', variables.partner_id],
      });
    },
  });
}

// ─── Batch add work orders ───────────────────────────────────────

export interface BatchAddWorkOrdersParams {
  count: number;
  /** container_option_id of the Product Type applied to every new row */
  product_type_option_id: string;
}

export function useBatchAddWorkOrders(projectId: string) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<BatchProgress | null>(null);

  const mutation = useMutation({
    mutationFn: async (params: BatchAddWorkOrdersParams) => {
      const total = params.count;
      setProgress({ done: 0, total });

      let latest: ProjectWorkOrderDetail[] = [];
      let done = 0;
      while (done < total) {
        const chunkCount = Math.min(PROJECT_PLANNING_BATCH_CHUNK_SIZE, total - done);
        const res = await projectPlanningControllerBatchAddWorkOrders(projectId, {
          ...params,
          count: chunkCount,
        });
        latest = res.data as ProjectWorkOrderDetail[];
        done += chunkCount;
        setProgress({ done, total });
        queryClient.setQueryData<ProjectPlanningDetail>(
          [PROJECT_PLANNING_KEY, 'detail', projectId],
          (old) => (old ? { ...old, work_orders: latest } : old),
        );
      }
      return latest;
    },
    onSettled: () => {
      setProgress(null);
    },
    onSuccess: () => {
      // Add-rows may have minted a brand-new product via
      // findOrCreateProductForSelections + createProductWithBoOTx. The
      // project-scoped BoO view filters its partner-wide matrix by the
      // project's work-order product_ids; if the matrix is still cached
      // from before this add, the new product won't appear.
      void queryClient.invalidateQueries({
        queryKey: [BILL_OF_OPERATIONS_KEY, 'matrix'],
      });
      // The partner-wide Production Planner enumerates the same work orders;
      // refresh it so the added rows show up there.
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTION_PLANNER_KEY],
      });
    },
  });

  return Object.assign(mutation, { progress });
}

// ─── Batch remove work orders ────────────────────────────────────

export function useBatchRemoveWorkOrders(projectId: string) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<BatchProgress | null>(null);

  const mutation = useMutation({
    mutationFn: async (workOrderIds: string[]) => {
      const total = workOrderIds.length;
      setProgress({ done: 0, total });

      let latest: ProjectWorkOrderDetail[] = [];
      for (let i = 0; i < total; i += PROJECT_PLANNING_BATCH_CHUNK_SIZE) {
        const chunk = workOrderIds.slice(i, i + PROJECT_PLANNING_BATCH_CHUNK_SIZE);
        const res = await projectPlanningControllerBatchRemoveWorkOrders(projectId, {
          work_order_ids: chunk,
        });
        latest = res.data as ProjectWorkOrderDetail[];
        setProgress({ done: Math.min(i + chunk.length, total), total });
        queryClient.setQueryData<ProjectPlanningDetail>(
          [PROJECT_PLANNING_KEY, 'detail', projectId],
          (old) => (old ? { ...old, work_orders: latest } : old),
        );
      }
      return latest;
    },
    onSettled: () => {
      setProgress(null);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [BILL_OF_OPERATIONS_KEY, 'matrix'],
      });
      // Removed rows must disappear from the partner-wide Production Planner.
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTION_PLANNER_KEY],
      });
    },
  });

  return Object.assign(mutation, { progress });
}

// ─── Update project metadata ─────────────────────────────────────
//
// Used by the Edit Project dialog. The backend rejects edits on
// released projects with 409; the mutation surfaces that distinctly so
// the dialog can keep itself open and explain why.

export interface UpdateProjectPlanningParams {
  project_name?: string;
  project_code?: string;
  project_prefix?: string | null;
  project_scheduled_start_date?: string | null;
  project_scheduled_complete_date?: string | null;
}

export class ProjectNotEditableError extends Error {
  constructor() {
    super('Only draft or redrafting projects can be edited');
    this.name = 'ProjectNotEditableError';
  }
}

export function useUpdateProjectPlanning(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateProjectPlanningParams) => {
      try {
        const res = await projectPlanningControllerUpdateProjectPlanning(projectId, params);
        return res.data as ProjectPlanningDetail;
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 409) {
          throw new ProjectNotEditableError();
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PROJECT_PLANNING_KEY, 'detail', projectId], data);
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'list'],
      });
      // project_name and other project-level fields are joined into the
      // partner-wide planner rows; refresh PP so renamed projects show their
      // new name without waiting for the 60s staleTime.
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTION_PLANNER_KEY],
      });
    },
  });
}

// ─── Update project column layout ────────────────────────────────
//
// Persists the AG Grid column state for the project planning grid.
// Allowed on any project status — column layout is presentation-only.
//
// Optimistic concurrency: the server compares `expected_column_config_updated_at`
// to the row's current `project_column_config_updated_at` and returns 409 if
// another writer has saved since the client last read. On 409 we just
// invalidate the detail query — the editor's live grid state is held in a
// snapshot ref that's deliberately decoupled from the cache, so the user's
// in-progress layout isn't disturbed and the next save will carry the
// refreshed token.

export class ProjectColumnConfigConflictError extends Error {
  constructor() {
    super('Project column config was updated by another user');
    this.name = 'ProjectColumnConfigConflictError';
  }
}

export interface UpdateProjectColumnConfigParams {
  column_state: ProjectColumnState[];
  expected_column_config_updated_at: string;
}

export function useUpdateProjectColumnConfig(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateProjectColumnConfigParams) => {
      try {
        const res = await projectPlanningControllerUpdateProjectColumnConfig(projectId, params);
        return res.data as ProjectPlanningDetail;
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 409) {
          throw new ProjectColumnConfigConflictError();
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PROJECT_PLANNING_KEY, 'detail', projectId], data);
    },
    onError: (err) => {
      if (err instanceof ProjectColumnConfigConflictError) {
        // Refresh the cached detail so the next save sends the up-to-date
        // token. We deliberately do NOT touch the AG Grid state here — the
        // editor's snapshot ref absorbs the user's in-progress layout and
        // isn't bound to this cache entry, so this never causes a flip-back.
        void queryClient.invalidateQueries({
          queryKey: [PROJECT_PLANNING_KEY, 'detail', projectId],
        });
      }
    },
  });
}

// ─── Release project ─────────────────────────────────────────────

export interface ReleaseProjectArgs {
  /**
   * ENG-608 workload gate resolution: 'apply_suggested' confirms every
   * suggestion inside the release transaction; 'none' releases with zero
   * categories (rejected when some products ARE confirmed). Omitted =
   * the backend blocks with UNCONFIRMED_WORKLOAD_CATEGORIES when any
   * product is unconfirmed.
   */
  workloadCategories?: 'apply_suggested' | 'none';
}

export function useReleaseProject(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: ReleaseProjectArgs = {}) => {
      const res = await projectPlanningControllerReleaseProject(
        projectId,
        args.workloadCategories !== undefined
          ? { workload_categories: args.workloadCategories }
          : {},
      );
      return res.data as ProjectPlanningDetail;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PROJECT_PLANNING_KEY, 'detail', projectId], data);
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'list'],
      });
      void queryClient.invalidateQueries({
        queryKey: [BILL_OF_OPERATIONS_KEY, 'matrix'],
      });
      // Release flips every work order's status from `draft` → `scheduled`;
      // refresh the partner-wide planner so status chips and editability
      // re-derive immediately.
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTION_PLANNER_KEY],
      });
    },
  });
}

// Releases the work orders still in `draft` on an already-released
// (redrafting) project — the rows added during a redraft cycle. The backend
// validates required fields (400 MISSING_REQUIRED_FIELDS, same shape as
// releaseProject), renumbers each factory's sequence, and flips them
// `draft` → `scheduled`. Used by the re-release flow alongside
// `useReReleaseWorkOrders`.
export function useReleaseDraftWorkOrders(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await projectPlanningControllerReleaseDraftWorkOrders(projectId);
      return res.data as ProjectPlanningDetail;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PROJECT_PLANNING_KEY, 'detail', projectId], data);
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'list'],
      });
      void queryClient.invalidateQueries({
        queryKey: [BILL_OF_OPERATIONS_KEY, 'matrix'],
      });
      // Releasing drafts flips their status to `scheduled`; refresh the
      // partner-wide planner so status chips and editability re-derive.
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTION_PLANNER_KEY],
      });
    },
  });
}

// ─── ENG-608 product workload categories ─────────────────────────

interface ConfirmProjectProductCategoryArgs {
  productId: string;
  /** 1..5 to confirm for every work order of the product; null to clear. */
  confirmedCategory: number | null;
}

/**
 * Confirm (or clear) the workload category for a product across ALL its
 * work orders in the project — workload category is a property of the
 * product, not the row. Optimistically patches the product's entry in the
 * planning detail cache (one patch updates every sibling row); the server
 * returns the authoritative detail which replaces the cache on success.
 */
export function useConfirmProjectProductCategory(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ productId, confirmedCategory }: ConfirmProjectProductCategoryArgs) => {
      const res = await projectPlanningControllerConfirmProductWorkloadCategory(
        projectId,
        productId,
        { confirmed_workload_category: confirmedCategory },
      );
      return res.data as ProjectPlanningDetail;
    },
    onMutate: async ({ productId, confirmedCategory }) => {
      const queryKey = [PROJECT_PLANNING_KEY, 'detail', projectId] as const;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ProjectPlanningDetail>(queryKey);
      if (previous) {
        queryClient.setQueryData<ProjectPlanningDetail>(queryKey, {
          ...previous,
          product_workload_categories: previous.product_workload_categories.map((entry) =>
            entry.product_id === productId
              ? {
                  ...entry,
                  confirmed_category: confirmedCategory,
                  confirmation_state:
                    confirmedCategory !== null ? ('confirmed' as const) : ('suggested' as const),
                  previous_confirmed_category:
                    confirmedCategory !== null ? null : entry.confirmed_category,
                }
              : entry,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _args, context) => {
      if (context?.previous) {
        queryClient.setQueryData([PROJECT_PLANNING_KEY, 'detail', projectId], context.previous);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PROJECT_PLANNING_KEY, 'detail', projectId], data);
      // Project confirmations surface on the matrix products too.
      void queryClient.invalidateQueries({ queryKey: [BILL_OF_OPERATIONS_KEY, 'matrix'] });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'detail', projectId],
      });
    },
  });
}

/**
 * ENG-608 Apply All Suggested — confirm the suggestion for every
 * unconfirmed product in the project. Reused by the release gate's
 * "Apply all suggested and release" chain.
 */
export function useApplyAllSuggestedForProjectProducts(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await projectPlanningControllerApplySuggestedWorkloadCategories(projectId);
      return res.data as ProjectPlanningDetail;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PROJECT_PLANNING_KEY, 'detail', projectId], data);
      void queryClient.invalidateQueries({ queryKey: [BILL_OF_OPERATIONS_KEY, 'matrix'] });
    },
  });
}

export interface UnconfirmedCategoryProduct {
  product_id: string;
  product_key: string;
  suggested_workload_category: number;
  data_state: 'baseline' | 'estimated' | 'forecasted';
}

/**
 * Defensive parse of the release endpoint's 400 payload when products
 * lack confirmed workload categories. Mirrors `parseMissingRequiredFields`
 * in the release route — the gate normally pre-empts this client-side,
 * but the backend enforces regardless.
 */
export function parseUnconfirmedCategories(error: unknown): UnconfirmedCategoryProduct[] | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  const body = error.bodyAsRecord;
  if (!body || body['code'] !== 'UNCONFIRMED_WORKLOAD_CATEGORIES') return null;
  const rows = body['products'];
  if (!Array.isArray(rows)) return null;
  const result: UnconfirmedCategoryProduct[] = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    if (typeof r['product_id'] !== 'string') continue;
    result.push({
      product_id: r['product_id'],
      product_key: typeof r['product_key'] === 'string' ? r['product_key'] : r['product_id'],
      suggested_workload_category:
        typeof r['suggested_workload_category'] === 'number' ? r['suggested_workload_category'] : 3,
      data_state:
        r['data_state'] === 'estimated' || r['data_state'] === 'forecasted'
          ? r['data_state']
          : 'baseline',
    });
  }
  return result.length > 0 ? result : null;
}

// ─── Reopen / re-release work orders ─────────────────────────────
//
// Released work orders ('scheduled') can be reopened for system-column edits,
// which flips them to 'redrafting'. Re-release sends them back to 'scheduled'.
// Both are bulk endpoints scoped by partner_id. The planner invalidates the
// detail query on success so the row badges and editability re-derive.

interface RedraftingTransitionResult {
  updated: { work_order_id: string; work_order_current_status: string }[];
  unchanged: { work_order_id: string; work_order_current_status: string }[];
}

// Shared chunked-status-transition shape used by both reopen-for-edit and
// re-release. Mirrors `useBatchAddWorkOrders` / `useBatchRemoveWorkOrders`:
// payloads are split into `PROJECT_PLANNING_BATCH_CHUNK_SIZE` slices so the
// backend's per-request DTO cap (5× this chunk size) isn't tripped on large
// projects, the UI can show progress, and each chunk lands in its own DB
// transaction. Fail-fast: a mid-flight chunk error throws — earlier chunks
// stay committed and the error message carries how many WOs succeeded
// before the failure so the toast can surface it.
function useChunkedStatusTransition(
  projectId: string,
  partnerId: string,
  endpointSegment: 'reopen-for-edit' | 're-release',
) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<BatchProgress | null>(null);

  const mutation = useMutation({
    mutationFn: async (workOrderIds: string[]) => {
      const total = workOrderIds.length;
      const aggregated: RedraftingTransitionResult = { updated: [], unchanged: [] };
      if (total === 0) return aggregated;

      setProgress({ done: 0, total });

      for (let i = 0; i < total; i += PROJECT_PLANNING_BATCH_CHUNK_SIZE) {
        const chunk = workOrderIds.slice(i, i + PROJECT_PLANNING_BATCH_CHUNK_SIZE);
        try {
          const res =
            endpointSegment === 'reopen-for-edit'
              ? await workOrdersPartnerControllerReopenForEdit(partnerId, {
                  work_order_ids: chunk,
                })
              : await workOrdersPartnerControllerReRelease(partnerId, {
                  work_order_ids: chunk,
                });
          const data = res.data as RedraftingTransitionResult;
          aggregated.updated.push(...data.updated);
          aggregated.unchanged.push(...data.unchanged);
          setProgress({ done: Math.min(i + chunk.length, total), total });
        } catch (err) {
          const done = aggregated.updated.length + aggregated.unchanged.length;
          if (done > 0) {
            const baseMessage = err instanceof Error ? err.message : String(err);
            throw new Error(
              `${baseMessage} — completed ${done} of ${total} work order${total === 1 ? '' : 's'} before the failure`,
            );
          }
          throw err;
        }
      }

      return aggregated;
    },
    // Invalidate in onSettled rather than onSuccess so a partially-completed
    // run (some chunks committed before a later chunk threw) still refreshes
    // the planner — those WOs really did transition on the backend.
    onSettled: () => {
      setProgress(null);
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'detail', projectId],
      });
      // The project list carries a derived `is_redrafting` per row that
      // drives the index page's status chip. Refresh it so the badge
      // flips in sync with this transition.
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'list'],
      });
      // Status transitions flip work_order_current_status, which the
      // partner-wide planner uses to drive status chips and editability.
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTION_PLANNER_KEY],
      });
    },
  });

  return Object.assign(mutation, { progress });
}

export function useReopenForEdit(projectId: string, partnerId: string) {
  return useChunkedStatusTransition(projectId, partnerId, 'reopen-for-edit');
}

export function useReReleaseWorkOrders(projectId: string, partnerId: string) {
  return useChunkedStatusTransition(projectId, partnerId, 're-release');
}

// ─── Archive / Unarchive work orders ─────────────────────────────
//
// Archiving soft-deletes scheduled/redrafting work orders (the backend
// enforces the status rule and reports anything it skipped). Unarchive is the
// inverse. Both invalidate the detail query (prefix match covers the archived
// and non-archived cache variants), the project list, the BoO matrix (the
// "behind" / unreleased counts shift), and the partner-wide planner.

interface SkippedWorkOrder {
  work_order_id: string;
  work_order_current_status: string;
}

export interface ArchiveWorkOrdersResult {
  archived_ids: string[];
  skipped: SkippedWorkOrder[];
}

export interface UnarchiveWorkOrdersResult {
  unarchived_ids: string[];
  skipped: SkippedWorkOrder[];
}

function invalidateAfterArchiveChange(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
) {
  void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'detail', projectId] });
  void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'list'] });
  void queryClient.invalidateQueries({ queryKey: [BILL_OF_OPERATIONS_KEY, 'matrix'] });
  void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY] });
}

export function useArchiveWorkOrders(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (workOrderIds: string[]) => {
      const res = await projectPlanningControllerArchiveWorkOrders(projectId, {
        work_order_ids: workOrderIds,
      });
      return res.data as ArchiveWorkOrdersResult;
    },
    onSettled: () => {
      invalidateAfterArchiveChange(queryClient, projectId);
    },
  });
}

export function useUnarchiveWorkOrders(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (workOrderIds: string[]) => {
      const res = await projectPlanningControllerUnarchiveWorkOrders(projectId, {
        work_order_ids: workOrderIds,
      });
      return res.data as UnarchiveWorkOrdersResult;
    },
    onSettled: () => {
      invalidateAfterArchiveChange(queryClient, projectId);
    },
  });
}
